import React, { useEffect, useState, useMemo } from 'react';
import { Select, MenuItem, Box, Paper, LinearProgress, Typography, Grid } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import { useProjectionsVsActual } from './useProjectionsVsActual';
import { useNotifier } from "muinotify";
import { exportToExcel } from '../../../components/export/exportToExcel';
import { exportToPdf } from '../../../components/export/exportToPdf';
import { exportToCsv } from '../../../components/export/exportToCsv';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import useSWR from 'swr';

const ProjectionsVsActual = () => {
  const { notifier } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const { category_item, error, mutate } = useProjectionsVsActual(searchQuery);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});

  const { data: productions, error: productionsError } = useSWR('/productions');
  const { data: categoryItems, error: categoryError } = useSWR(
    selectedProduction?.id
      ? `/production-category-item/of-production/${selectedProduction.id}`
      : null
  );
  const { data: itemsDetails, error: detailsError } = useSWR(
    selectedProduction?.id && categoryItems
      ? `/expense/test/${selectedProduction.id}/${categoryItems.map((item) => item.id).join(",")}`
      : null,
    async () => {
      const token = localStorage.getItem("token");

      try {
        const itemRequests = categoryItems.map((item) =>
          fetch(
            `${process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT ? '' : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`}/api/expense/test/${selectedProduction.id}/${item.id}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          )
            .then((res) => {
              if (!res.ok) {
                throw new Error(
                  `Error fetching item ${item.id}: ${res.statusText}`
                );
              }
              return res.json();
            })
            .catch((error) => {
              console.error(`Error fetching item ${item.id}:`, error);
              return null; // Return null for this item if the fetch fails
            })
        );

        const results = await Promise.all(itemRequests);

        // Filter out null responses to avoid processing invalid data
        return results.filter((item) => item !== null);
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error; // Let SWR handle the error
      }
    },
    { refreshInterval: 3700 }
  );

  useEffect(() => {
    const savedProduction = localStorage.getItem('selectedProduction');
    if (savedProduction && productions) {
      const parsedProduction = JSON.parse(savedProduction);
      const production = productions.find(p => p.id === parsedProduction.id);
      if (production) {
        setSelectedProduction(production);
      }
    }
  }, [productions]);

  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem('selectedProduction', JSON.stringify(selectedProduction));
    }
  }, [selectedProduction]);

  const handleProductionChange = (event) => {
    setSelectedProduction(productions.find((p) => p.id === event.target.value));
  };

  // Calculate the per-episode budget sum
  const totalBudget = useMemo(() => {
    return categoryItems?.reduce((sum, item) => sum + (item.pci_amount || 0), 0) || 0;
  }, [categoryItems]);

  // Now create a helper that accepts the correct budget value
  const calculatePercentageDifference = (actualCost, budget) => {
    return budget > 0 ? ((actualCost - budget) / budget) * 100 : 0;
  };

  // Helper to choose text color based on actual vs. budget
  const getTextColor = (actualCost, budget) => {
    if (actualCost > budget)
      return 'red';
    if (actualCost < budget)
      return 'green';
    return 'black';
  };

  const columns = [
    {
      field: 'episode',
      headerName: 'Επεισόδιο',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 250,
      renderCell: (params) => {
        const isSpecialValue = params.value === 'Σύνολο';
        const displayValue = isSpecialValue ? params.value : `E-${params.value}`;
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: isSpecialValue ? 'bold' : 'normal',
            }}
          >
            {displayValue}
          </div>
        );
      },
    },
    {
      field: 'budget',
      headerName: 'Προϋπολογισμός',
      headerAlign: 'center',
      flex: 1,
      minWidth: 80,
      maxWidth: 180,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            color: getTextColor(params.row.actualCost, params.row.budget),
          }}
        >
          {params.row.formattedBudget}
        </div>
      ),
    },
    {
      field: 'actualCost',
      headerName: 'Πραγματικό Κόστος',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 200,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            color: getTextColor(params.row.actualCost, params.row.budget),
          }}
        >
          {params.row.formattedActualCost}
        </div>
      ),
    },
    {
      field: 'unamortizedValue',
      headerName: 'Αναπόσβεστη Αξία',
      width: 180,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {params.row.formattedUnamortizedValue}
        </div>
      ),
    },
    {
      field: 'graph',
      headerName: 'Τελική Απώλεια',
      headerAlign: 'center',
      flex: 1,
      minWidth: 100,
      maxWidth: 300,
      renderCell: (params) => {
        const rowBudget = params.row.budget;
        const percentage = rowBudget > 0 ? (params.row.actualCost / rowBudget) * 100 : 0;
        const percentageDiff = calculatePercentageDifference(params.row.actualCost, rowBudget);
        return (
          <Box display="flex" alignItems="center">
            <Box width="100%" mr={1}>
              <LinearProgress
                variant="determinate"
                value={percentage}
                color={params.row.actualCost > rowBudget ? "error" : (params.row.actualCost < rowBudget ? "success" : "inherit")}
                sx={{ height: 8 }}
              />
            </Box>
            <Typography
              variant="body2"
              color={percentageDiff > 0 ? "error" : "inherit"}
              sx={{ width: 50, textAlign: 'right' }}
            >
              {percentageDiff.toFixed(2)}%
            </Typography>
          </Box>
        );
      },
    },
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const calculateActualCost = (itemsDetails) => {
    // Calculate the total actual cost per episode
    const episodeCosts = {};
    // Flatten itemsDetails array (array of arrays) into a single array
    const flatItemsDetails = itemsDetails.flat();
    
    flatItemsDetails.forEach((item) => {
      if (item.exp_assigned_episodes && Array.isArray(item.exp_assigned_episodes)) {
        item.exp_assigned_episodes.forEach((episode) => {
          if (!episodeCosts[episode]) {
            episodeCosts[episode] = 0;
          }
          if (item.expense_details && Array.isArray(item.expense_details)) {
            const episodeCost = item.expense_details
              .filter((detail) => detail.exp_d_episode === episode)
              .reduce((sum, detail) => sum + (parseFloat(detail.exp_d_amount) || 0), 0);
            episodeCosts[episode] += episodeCost;
          }
        });
      }
    });
    return episodeCosts;
  };

  const calculateData = () => {
    if (!selectedProduction || !categoryItems || !itemsDetails) {
      setRows([]);
      setSummary({});
      return;
    }

    const episodeCount = selectedProduction.no_of_episodes || 0;
    const totalBudgetPerEpisode = categoryItems.reduce((sum, item) => sum + (item.pci_amount || 0), 0);

    // Calculate unamortized values using the same method as production_status-5
    // For each episode, calculate the sum of maxEpAm (expenses assigned to all episodes) across all category items
    const unamortizedValuesPerEpisode = Array.from({ length: episodeCount }, (_, episodeIndex) => {
      const episode = episodeIndex + 1;
      
      // Calculate maxEpAm for each category item for this episode
      const totalOfFullAssigned = categoryItems.reduce((sum, item) => {
        const itemDetailsList = itemsDetails
          .flat()
          .filter((detail) => detail.pci_id === item.id);

        let maxEpAm = 0;
        itemDetailsList.forEach((itemDetail) => {
          // Only consider expenses assigned to ALL episodes
          if (itemDetail.exp_assigned_episodes?.length === episodeCount) {
            const expenses = itemDetail.expense_details.filter(
              (expense) => expense.exp_d_episode === episode
            );
            const totalExpense = expenses.reduce(
              (sum, expense) => sum + (parseFloat(expense.exp_d_amount) || 0),
              0
            );
            maxEpAm += totalExpense;
          }
        });

        return sum + maxEpAm;
      }, 0);

      // Unamortized value = totalOfFullAssigned * remaining episodes
      const unamortizedValue = totalOfFullAssigned * (episodeCount - episodeIndex);
      return {
        episode,
        unamortizedValue,
      };
    });

    const actualCosts = calculateActualCost(itemsDetails);

    const generatedRows = Array.from({ length: episodeCount }, (_, episodeIndex) => {
      const episode = episodeIndex + 1;
      const unamortizedValue = unamortizedValuesPerEpisode.find((item) => item.episode === episode)?.unamortizedValue || 0;
      const budget = totalBudgetPerEpisode;
      const actualCost = actualCosts[episode] || 0;
      return {
        id: episode,
        episode,
        budget, // raw value
        formattedBudget: formatCurrency(budget), // formatted value
        actualCost, // raw value
        formattedActualCost: formatCurrency(actualCost), // formatted value
        unamortizedValue, // raw value
        formattedUnamortizedValue: formatCurrency(unamortizedValue), // formatted value
      };
    });

    // Summary row with formatted values
    const overallBudget = totalBudgetPerEpisode * episodeCount;
    const totals = generatedRows.reduce(
      (acc, row) => ({
        totalActualCost: acc.totalActualCost + (row.actualCost || 0),
        totalUnamortizedValue: acc.totalUnamortizedValue + row.unamortizedValue,
        highestActualCost: Math.max(acc.highestActualCost, row.actualCost || 0),
        lowestActualCost: Math.min(acc.lowestActualCost, row.actualCost || Infinity),
      }),
      { totalActualCost: 0, totalUnamortizedValue: 0, highestActualCost: -Infinity, lowestActualCost: Infinity }
    );

    const summaryRow = {
      id: 'summary',
      episode: 'Σύνολο',
      budget: overallBudget,
      formattedBudget: formatCurrency(overallBudget),
      actualCost: totals.totalActualCost,
      formattedActualCost: formatCurrency(totals.totalActualCost),
      unamortizedValue: totals.totalUnamortizedValue,
      formattedUnamortizedValue: formatCurrency(totals.totalUnamortizedValue),
    };

    setRows([...generatedRows, summaryRow]);
    setSummary(totals);
  };

  useEffect(() => {
    calculateData();
  }, [selectedProduction, categoryItems, itemsDetails]);

  const columnsToExtract = [
    { field: 'episode', headerName: 'Επεισόδιο' },
    { field: 'formattedBudget', headerName: 'Προϋπολογισμός' },
    { field: 'formattedActualCost', headerName: 'Πραγματικό Κόστος' },
    { field: 'formattedUnamortizedValue', headerName: 'Αναπόσβεστη Αξία' },
  ];

  const handleExportToExcel = () => {
    exportToExcel(
      rows,
      columnsToExtract,
      `Προβλεπόμενο Κόστος έναντι Πραγματικού Κόστους`
    );
  };

  const handleExportToPdf = () => {
    exportToPdf(
      rows,
      columnsToExtract,
      `Προβλεπόμενο Κόστος έναντι Πραγματικού Κόστους`
    );
  };

  const handleExportToCsv = () => {
    exportToCsv(
      rows,
      columnsToExtract,
      `Προβλεπόμενο Κόστος έναντι Πραγματικού Κόστους`
    );
  };

  const isLoading = selectedProduction && (!categoryItems || !itemsDetails);

  if (productionsError || categoryError || detailsError) {
    return <Typography>Error loading data.</Typography>;
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="Category Items" module="Productions" subtitle="List of category items" />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>

          <FormListToolbar
            disableAdd={true}
            handleExportToExcel={handleExportToExcel}
            handleExportToPdf={handleExportToPdf}
            handleExportToCsv={handleExportToCsv}
          />

          <Box m="8px 0 0 0" height="80vh">
            <Paper elevation={2} style={{ padding: "1rem", width: "100%", height: "80vh" }}>

              {/* Select Production and DataGrid */}
              <FormControl fullWidth size="small">
                <InputLabel id="prod-label">Παραγωγή</InputLabel>
                <Select
                  labelId="prod-label"
                  id="prod-id"
                  label="Παραγωγή"
                  fullWidth
                  size="small"
                  value={selectedProduction?.id || ''}
                  onChange={handleProductionChange}
                  style={{ width: '20%', marginBottom: '16px' }}
                >
                  {productions?.map((production) => (
                    <MenuItem key={production.id} value={production.id}>
                      {production.p_descr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isLoading && <LinearProgress />}

              {error && <div>{error}</div>}

              {!selectedProduction && !isLoading && (
                <>
                  <DataGrid
                    sx={{
                      height: '69vh',
                      '& .MuiDataGrid-cell': {
                        borderRight: '1px solid #ddd',
                      },
                      '& .MuiDataGrid-columnHeader': {
                        borderRight: '1px solid #ddd',
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                      },
                    }}
                    rows={rows}
                    columns={columns}
                    pageSize={5}
                    getRowId={(row) => row.id}
                    pagination
                  />
                </>
              )}

              {selectedProduction && (
                <>
                  <DataGrid
                    sx={{
                      height: '69vh',
                      '& .MuiDataGrid-cell': {
                        borderRight: '1px solid #ddd',
                        borderBottom: 'none',
                      },
                      '& .MuiDataGrid-columnHeader': {
                        borderRight: '1px solid #ddd',
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                      },
                    }}
                    rows={rows}
                    columns={columns}
                    pageSize={5}
                    getRowId={(row) => row.id}
                    pagination
                  />
                </>
              )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectionsVsActual;
